import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Card, Input, PageHeader, Pagination, Select } from '../../design';
import { queryParams, useUrlState } from '../../lib/urlState';
import { useCount, useLockerOptions } from '../operations/common';
import { Kpi } from '../operations/OperationsPage';
import os from '../operations/operations.module.css';
import { useOrgOptions } from '../users/UsersPage';
import { CLASSIFICATION, type DocumentClassification, DocumentsTable } from './common';
import { UploadDialog } from './UploadDialog';

type Status = 'available' | 'due' | 'deleted';

function count(q: { status?: Status; classification?: DocumentClassification }) {
  return () =>
    unwrap(api.GET('/documents', { params: { query: { ...q, pageSize: 1 } } })) as Promise<{
      total: number;
    }>;
}

/** Documente (spec §4.5): metadate, clasificare, retenție; fișierele stau în stocarea S3, nu în API. */
export function DocumentsPage() {
  const { user, has } = useAuth();
  const canEdit = has('documents');
  const { values, page, pageSize, set } = useUrlState([
    'q',
    'classification',
    'status',
    'lockerId',
    'orgId',
  ] as const);
  const orgs = useOrgOptions();
  const lockers = useLockerOptions(values.orgId || undefined);
  const [uploading, setUploading] = useState(false);

  const available = useCount(['documents', 'count', 'available'], count({}));
  const due = useCount(['documents', 'count', 'due'], count({ status: 'due' }));
  const confidential = useCount(
    ['documents', 'count', 'confidential'],
    count({ classification: 'confidential' }),
  );
  const deleted = useCount(['documents', 'count', 'deleted'], count({ status: 'deleted' }));

  const query = useQuery({
    queryKey: ['documents', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/documents', {
          params: {
            query: queryParams({
              q: values.q || undefined,
              classification: (values.classification || undefined) as
                DocumentClassification | undefined,
              status: (values.status || undefined) as Status | undefined,
              lockerId: values.lockerId || undefined,
              orgId: values.orgId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
  });

  return (
    <>
      <PageHeader
        title="Documente"
        subtitle="Procese-verbale, contracte, fotografii: încărcate direct în stocare, clasificate, cu retenție per organizație."
        actions={
          canEdit ? (
            <Button variant="primary" icon="plus" onClick={() => setUploading(true)}>
              Încarcă document
            </Button>
          ) : undefined
        }
      />
      <div className={os.kpis}>
        <Kpi label="Disponibile" value={available.total} hint="În perimetrul tău" />
        <Kpi
          label="La termen"
          value={due.total}
          hint="Retenția a expirat: revizuiește"
          tone="orange"
        />
        <Kpi label="Confidențiale" value={confidential.total} hint="Acces doar cu permisiune" />
        <Kpi label="Șterse recent" value={deleted.total} hint="Dispar definitiv după 30 de zile" />
      </div>
      <Card tight>
        <div className="flex wrap" style={{ padding: 16 }}>
          <Input
            placeholder="Caută după nume…"
            defaultValue={values.q}
            onChange={(e) => set({ q: e.target.value })}
            style={{ maxWidth: 260 }}
            aria-label="Căutare"
          />
          <Select
            value={values.classification}
            onChange={(e) => set({ classification: e.target.value })}
            style={{ maxWidth: 200 }}
            aria-label="Clasificare"
          >
            <option value="">Toate clasificările</option>
            {(Object.keys(CLASSIFICATION) as DocumentClassification[]).map((c) => (
              <option key={c} value={c}>
                {CLASSIFICATION[c].label}
              </option>
            ))}
          </Select>
          <Select
            value={values.status}
            onChange={(e) => set({ status: e.target.value })}
            style={{ maxWidth: 200 }}
            aria-label="Stare"
          >
            <option value="">Disponibile</option>
            <option value="due">La termen</option>
            <option value="deleted">Șterse (30 de zile)</option>
          </Select>
          <Select
            value={values.lockerId}
            onChange={(e) => set({ lockerId: e.target.value })}
            style={{ maxWidth: 220 }}
            aria-label="Locker"
          >
            <option value="">Toate lockerele</option>
            {lockers.data?.items.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
          {user?.orgId === null && (
            <Select
              value={values.orgId}
              onChange={(e) => set({ orgId: e.target.value, lockerId: null })}
              style={{ maxWidth: 220 }}
              aria-label="Organizație"
            >
              <option value="">Toate organizațiile</option>
              {orgs.data?.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          )}
        </div>
        <DocumentsTable
          rows={query.data?.items ?? []}
          canEdit={canEdit}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Niciun document. Încarcă unul din butonul de sus sau din detaliul unei operațiuni.'
          }
        />
        {query.data && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={query.data.total}
            onPage={(p) => set({ page: String(p) })}
          />
        )}
      </Card>
      {uploading && (
        <UploadDialog onClose={() => setUploading(false)} onUploaded={() => setUploading(false)} />
      )}
    </>
  );
}
